import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PackingListUpload.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function PackingListUpload({ token }) {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    // Shipment Details
    client_name: '',
    shesha_reference: '',
    sfr_number: '',
    bill_of_lading: '',
    vessel_voyage_number: '',
    container_count: '',
    container_type: 'FCL',
    gross_weight_lcl: '',
    delivery_address: '',
    // Packing List Details
    supplier_id: '',
    order_number: '',
    invoice_number: '',
    client_po_number: '',
    file: null
  });

  useEffect(() => {
    if (token) {
      fetchSuppliers();
    }
  }, [token]);

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${API_URL}/suppliers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load suppliers');

      const data = await response.json();
      setSuppliers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Error loading suppliers: ' + err.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      file: e.target.files[0]
    }));
  };

  const handleContainerTypeChange = (e) => {
    setFormData(prev => ({
      ...prev,
      container_type: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!formData.client_name || !formData.sfr_number) {
        setError('Client name and SFR number are required');
        setLoading(false);
        return;
      }

      if (!formData.supplier_id) {
        setError('Supplier is required');
        setLoading(false);
        return;
      }

      if (!formData.file) {
        setError('PDF file is required');
        setLoading(false);
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append('supplier_id', formData.supplier_id);
      formDataToSend.append('order_number', formData.order_number);
      formDataToSend.append('invoice_number', formData.invoice_number);
      formDataToSend.append('client_po_number', formData.client_po_number);
      formDataToSend.append('file', formData.file);
      
      // Shipment fields
      formDataToSend.append('client_name', formData.client_name);
      formDataToSend.append('shesha_reference', formData.shesha_reference);
      formDataToSend.append('sfr_number', formData.sfr_number);
      formDataToSend.append('bill_of_lading', formData.bill_of_lading);
      formDataToSend.append('vessel_voyage_number', formData.vessel_voyage_number);
      formDataToSend.append('container_count', formData.container_count);
      formDataToSend.append('container_type', formData.container_type);
      formDataToSend.append('gross_weight_lcl', formData.gross_weight_lcl);
      formDataToSend.append('delivery_address', formData.delivery_address);

      const response = await fetch(`${API_URL}/packing-lists/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to upload packing list');
        setLoading(false);
        return;
      }

      setSuccess('Packing list uploaded successfully!');
      setTimeout(() => {
        navigate(`/packing-list/${data.id}/cargo`);
      }, 1500);
    } catch (err) {
      setError('Error uploading packing list: ' + err.message);
      setLoading(false);
    }
  };

  return (
    <div className="packing-list-upload-container">
      <div className="upload-header">
        <h1>Record Inbound Shipment</h1>
        <p>Record shipment details and upload packing list PDF</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={handleSubmit} className="upload-form">
        
        {/* LEFT COLUMN: Shipment Details */}
        <div className="form-column">
          <fieldset className="form-section">
            <legend>Shipment Details</legend>

            <div className="form-group">
              <label htmlFor="clientName">Client Name *</label>
              <input
                id="clientName"
                type="text"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                placeholder="e.g., ABC Industries"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="sheshaRef">Shesha Reference</label>
              <input
                id="sheshaRef"
                type="text"
                name="shesha_reference"
                value={formData.shesha_reference}
                onChange={handleChange}
                placeholder="e.g., SR-2026-001"
              />
            </div>

            <div className="form-group">
              <label htmlFor="sfrNumber">SFR Number *</label>
              <input
                id="sfrNumber"
                type="text"
                name="sfr_number"
                value={formData.sfr_number}
                onChange={handleChange}
                placeholder="e.g., SFR-123456"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="bol">Bill of Lading</label>
              <input
                id="bol"
                type="text"
                name="bill_of_lading"
                value={formData.bill_of_lading}
                onChange={handleChange}
                placeholder="e.g., BOL-789456"
              />
            </div>

            <div className="form-group">
              <label htmlFor="vesselVoyage">Vessel/Voyage Number</label>
              <input
                id="vesselVoyage"
                type="text"
                name="vessel_voyage_number"
                value={formData.vessel_voyage_number}
                onChange={handleChange}
                placeholder="e.g., VESSEL-123/V456"
              />
            </div>

            <div className="form-group">
              <label htmlFor="containerCount">Container Count</label>
              <input
                id="containerCount"
                type="number"
                name="container_count"
                value={formData.container_count}
                onChange={handleChange}
                placeholder="e.g., 2"
              />
            </div>

            <div className="form-group">
              <label htmlFor="containerType">Container Type</label>
              <select
                id="containerType"
                name="container_type"
                value={formData.container_type}
                onChange={handleContainerTypeChange}
              >
                <option value="FCL">FCL (Full Container Load)</option>
                <option value="LCL">LCL (Less than Container Load)</option>
              </select>
            </div>

            {formData.container_type === 'LCL' && (
              <div className="form-group">
                <label htmlFor="grossWeight">Gross Weight (LCL) in kg</label>
                <input
                  id="grossWeight"
                  type="number"
                  name="gross_weight_lcl"
                  value={formData.gross_weight_lcl}
                  onChange={handleChange}
                  placeholder="e.g., 5000"
                  step="0.01"
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="deliveryAddress">Delivery Address</label>
              <textarea
                id="deliveryAddress"
                name="delivery_address"
                value={formData.delivery_address}
                onChange={handleChange}
                placeholder="Full delivery address"
                rows="3"
              />
            </div>
          </fieldset>
        </div>

        {/* RIGHT COLUMN: Packing List Details */}
        <div className="form-column">
          <fieldset className="form-section">
            <legend>Packing List Details</legend>

            <div className="form-group">
              <label htmlFor="supplier">Supplier *</label>
              <select
                id="supplier"
                name="supplier_id"
                value={formData.supplier_id}
                onChange={handleChange}
                required
              >
                <option value="">-- Select Supplier --</option>
                {suppliers.map(supplier => (
                  <option key={supplier.supplier_id} value={supplier.supplier_id}>
                    {supplier.supplier_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="orderNumber">Order Number</label>
              <input
                id="orderNumber"
                type="text"
                name="order_number"
                value={formData.order_number}
                onChange={handleChange}
                placeholder="e.g., PO-2026-001"
              />
            </div>

            <div className="form-group">
              <label htmlFor="invoiceNumber">Invoice Number</label>
              <input
                id="invoiceNumber"
                type="text"
                name="invoice_number"
                value={formData.invoice_number}
                onChange={handleChange}
                placeholder="e.g., INV-2026-001"
              />
            </div>

            <div className="form-group">
              <label htmlFor="clientPO">Client Purchase Order</label>
              <input
                id="clientPO"
                type="text"
                name="client_po_number"
                value={formData.client_po_number}
                onChange={handleChange}
                placeholder="e.g., PO-CLIENT-001"
              />
            </div>

            <div className="form-group">
              <label htmlFor="pdfFile">Packing List PDF *</label>
              <input
                id="pdfFile"
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                required
              />
              {formData.file && <small>Selected: {formData.file.name}</small>}
            </div>
          </fieldset>
        </div>

        <button type="submit" className="btn btn-primary btn-large" disabled={loading}>
          {loading ? 'Uploading...' : 'Record Shipment & Upload PDF'}
        </button>
      </form>
    </div>
  );
}
